import { BottomSheetModal } from '@gorhom/bottom-sheet';
import {
  ActionableBannerMessages,
  DebitCardActivationBannerMessage,
  DebitCardSetPinBannerMessage,
  IBannerMessageR2Typenames,
  InformationalBannerMessage,
  InformationalBannerMessageV2,
  NotificationBannerTile,
  RelinkPlaidAccountBannerMessage,
  SuspiciousCardTransactionBannerMessage,
  VaroAdvanceOverdueBannerMessage,
  VaroAdvancePendingPaymentBannerMessage,
} from '@varo/graphql-schema-types';
import { useRouter } from 'expo-router';
import {
  getAlertType,
  mapAlertType,
} from 'features/accounts/AccountDetails/AccountDetails.helpers';
import useDeleteExternalAccount from 'features/accounts/hooks/useDeleteExternalAccount';
import { PLAID_RELINK_SOURCE_NAMES } from 'features/plaid/constants/PlaidSourceNames';
import usePlaidLauncher from 'features/plaid/hooks/usePlaidLauncher';
import { RefObject, useContext, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { StyleSheet, View } from 'react-native';
import { HTMLRenderer } from 'shared/components/HTMLRenderer/HTMLRenderer';
import Dialog from 'shared/components/vxl/Dialog/Dialog';
import InlineAlert from 'shared/components/vxl/InlineAlert/InlineAlert';
import LoadingSpinner from 'shared/components/vxl/loading/LoadingSpinner/LoadingSpinner';
import { usePressableAnalytics } from 'shared/hooks/analytics/usePressableAnalytics';
import { useViewDidFocusScreenAnalyticsEvent } from 'shared/hooks/analytics/useViewDidFocusAnalytics';
import { PlaidIntent } from 'shared/hooks/usePlaidV2/types';
import { GlobalUIContext } from 'shared/providers/globalUIProvider/GlobalUIContextProvider/GlobalUIContextProvider';
import theme from 'shared/styles/theme';

export type BannerMessageResult =
  | DebitCardActivationBannerMessage
  | DebitCardSetPinBannerMessage
  | InformationalBannerMessage
  | InformationalBannerMessageV2
  | RelinkPlaidAccountBannerMessage
  | VaroAdvanceOverdueBannerMessage
  | VaroAdvancePendingPaymentBannerMessage
  | SuspiciousCardTransactionBannerMessage
  | ActionableBannerMessages;

interface NotificationBannerProps {
  bannerMessage: BannerMessageResult;
  bannerData?: NotificationBannerTile;
  bottomSheetRef: RefObject<BottomSheetModal | null> | null;
  accountId?: string | undefined;
  refreshAccountSummary: () => void;
}

const NotificationBanner = ({
  bannerMessage,
  bannerData,
  bottomSheetRef,
  refreshAccountSummary,
  accountId,
}: NotificationBannerProps) => {
  const router = useRouter();

  const { t: translatedText } = useTranslation(['accounts', 'myVaro']);
  const { setSnackBarMessage } = useContext(GlobalUIContext);

  const {
    startLaunch: startRelink,
    cancelLaunch: cancelRelink,
    error: relinkTokenError,
  } = usePlaidLauncher(
    accountId ? { intent: PlaidIntent.ReLink, accountId } : undefined,
    {
      sourceName: PLAID_RELINK_SOURCE_NAMES.AccountDetails,
      onResult: (result) => {
        if (result.status === 'success') {
          refreshAccountSummary();
          return;
        }

        if (result.status === 'error' || result.status === 'blocked') {
          setSnackBarMessage?.(
            translatedText('accounts:notificationBanner.relinkAccountFailed'),
          );
        }
      },
    },
  );

  const [dialogVisible, setDialogVisible] = useState(false);

  const { dialogButtons, removeAccountErrorModel, removeAccountLoading } =
    useDeleteExternalAccount({
      setDialogVisible,
      accountId,
    });

  const plaidRelinkPressEvent = usePressableAnalytics({
    analyticsId: 'relinkNotificationBanner',
  });

  const trackEvent = usePressableAnalytics({
    analyticsId: 'suspiciousTransaction',
    customAttributes: {
      alert_name: 'suspicious_transaction_alert_banner',
    },
  });

  useViewDidFocusScreenAnalyticsEvent(
    bannerMessage?.__typename ===
      IBannerMessageR2Typenames.SuspiciousCardTransactionBannerMessage
      ? {
          key: 'SuspiciousCardTransactionBanner',
          customAttributes: {
            alert_name: 'suspicious_transaction_alert_banner',
          },
        }
      : null,
  );

  useEffect(() => {
    if (removeAccountErrorModel) {
      setSnackBarMessage?.(removeAccountErrorModel.messageBody);
    }
  }, [setSnackBarMessage, removeAccountErrorModel]);

  useEffect(() => {
    if (relinkTokenError) {
      setSnackBarMessage?.(
        translatedText('accounts:notificationBanner.relinkAccountError'),
      );
      cancelRelink();
    }
  }, [cancelRelink, relinkTokenError, setSnackBarMessage, translatedText]);

  function handleCardPressAction() {
    if (bannerData) {
      return () => {
        bottomSheetRef?.current?.present();
      };
    }

    switch (bannerMessage?.__typename) {
      case IBannerMessageR2Typenames.DebitCardSetPinBannerMessage:
        return () => {
          router.push({
            pathname: '/myvaro/card/new-pin/[cardId]',
            params: {
              cardId: bannerMessage.cardId,
              cardType: 'debit_card',
            },
          });
        };

      case IBannerMessageR2Typenames.DebitCardActivationBannerMessage:
        return () => {
          router.push({
            pathname: '/myvaro/card/activate-card/[cardId]',
            params: {
              cardId: bannerMessage.cardId,
              cardType: 'debit_card',
            },
          });
        };

      case IBannerMessageR2Typenames.SuspiciousCardTransactionBannerMessage:
        return () => {
          trackEvent();
          router.push({
            pathname: '/suspicious-transaction',
            params: { id: bannerMessage.transactionId },
          });
        };

      case IBannerMessageR2Typenames.RelinkPlaidAccountBannerMessage:
        return () => {
          startRelink();
          plaidRelinkPressEvent();
        };

      case IBannerMessageR2Typenames.ActionableBannerMessages:
        return () => setDialogVisible(true);

      case IBannerMessageR2Typenames.InformationalBannerMessage:
      case IBannerMessageR2Typenames.VaroAdvanceOverdueBannerMessage:
        return undefined;
    }
  }

  return (
    <View style={styles.alertContainer}>
      <InlineAlert
        titleText={bannerData ? '' : bannerMessage?.title}
        bodyText={
          bannerData
            ? ((
                <HTMLRenderer htmlAsString={bannerData.message ?? ''} />
              ) as unknown as string)
            : bannerMessage.message
        }
        alertType={
          bannerData
            ? getAlertType(bannerData.color)
            : mapAlertType(bannerMessage.type)
        }
        onCardPress={handleCardPressAction()}
      />
      <Dialog
        titleText={translatedText('manageAccount.dialog.title')}
        messageText={bannerMessage?.message}
        isDialogVisible={dialogVisible}
        setModalVisible={setDialogVisible}
        dialogButtonProps={dialogButtons}
      />
      {removeAccountLoading ? (
        <View style={styles.loadingContainer}>
          <LoadingSpinner />
        </View>
      ) : null}
    </View>
  );
};

const styles = StyleSheet.create({
  alertContainer: {
    backgroundColor: theme.color.background.primary,
    padding: theme.spacing.m,
  },
  loadingContainer: {
    alignItems: 'center',
    backgroundColor: 'white',
    bottom: 0,
    justifyContent: 'center',
    left: 0,
    position: 'absolute',
    right: 0,
    top: 0,
    zIndex: 2,
  },
});

export default NotificationBanner;
